import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { tokenRefreshed, logout } from '../slices/authSlice';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://192.168.29.142:5001/api';

const rawBaseQuery = fetchBaseQuery({
  baseUrl: API_BASE_URL,
  prepareHeaders: (headers, { getState }) => {
    const token = getState()?.auth?.token || localStorage.getItem('token');
    if (token) {
      headers.set('authorization', `Bearer ${token}`);
    }
    return headers;
  },
  fetchFn: async (input, init) => {
    // If body is FormData, don't set Content-Type header
    if (init?.body instanceof FormData) {
      // Remove Content-Type to let browser set it with boundary
      if (init.headers) {
        if (init.headers instanceof Headers) {
          init.headers.delete('content-type');
        } else if (typeof init.headers === 'object') {
          delete init.headers['content-type'];
          delete init.headers['Content-Type'];
        }
      }
    }
    return fetch(input, init);
  },
});

let refreshPromise = null;

const baseQuery = async (args, api, extraOptions) => {
  let result = await rawBaseQuery(args, api, extraOptions);

  if (result.error && result.error.status === 401) {
    const url = typeof args === 'string' ? args : args?.url;
    // Don't loop refresh for auth endpoints
    if (url && (url.includes('/auth/login') || url.includes('/auth/refresh'))) {
      return result;
    }

    const currentRefreshToken = api.getState()?.auth?.refreshToken || localStorage.getItem('refreshToken');

    if (currentRefreshToken) {
      if (!refreshPromise) {
        refreshPromise = (async () => {
          try {
            const refreshRes = await fetch(`${API_BASE_URL}/auth/refresh-token`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ refreshToken: currentRefreshToken }),
            });
            const data = await refreshRes.json();
            if (refreshRes.ok && data?.token) {
              api.dispatch(tokenRefreshed(data));
              return data.token;
            }
            return null;
          } catch (err) {
            return null;
          } finally {
            refreshPromise = null;
          }
        })();
      }

      const newToken = await refreshPromise;

      if (newToken) {
        // Retry the original query with the refreshed access token
        result = await rawBaseQuery(args, api, extraOptions);
      } else {
        // Refresh token failed/expired -> log out cleanly
        api.dispatch(logout());
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('permissions');
        localStorage.removeItem('user');
        window.location.href = '/login';
      }
    } else {
      // No refresh token found -> log out
      api.dispatch(logout());
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('permissions');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
  }

  return result;
};

export const baseApi = createApi({
  reducerPath: 'api',
  baseQuery,
  tagTypes: [
    'User',
    'Company',
    'Module',
    'Product',
    'Inventory',
    'Purchase',
    'PurchaseReturn',
    'Sales',
    'SalesHolds',
    'Customer',
    'Supplier',
    'Finance',
    'Report',
    'VanSales',
    'Role',
    'Quotation',
    'EmailTemplate',
    'Backup',
    'RegisterSession',
    'WarrantyPeriod',
    'Imei',
    'RepairTicket',
    'TradeInTicket',
    'RmaTicket'
  ],
  endpoints: () => ({}),
});
